import React, { memo, useCallback } from 'react';
import { Chip } from '@mui/material';
import styled from 'styled-components';

import ControlledTextField from 'components/ControlledTextField';
import DeleteIconButton from 'components/DeleteIconButton';
import OptionalMandatoryChooser from 'components/OptionalMandatoryChooser';
import UpDown from 'components/UpDown';
import { Action } from 'state';
import Field from 'types/Field';

const StyledFieldForm = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto auto auto;
  grid-template-rows: auto;
  grid-template-areas:
    'upDown label isMandatory fieldType trash'
    '. defaultValue . . .';
  align-items: center;
  column-gap: 0.5rem;
  row-gap: 1rem;
`;

const StyledUpDown = styled(UpDown)`
  grid-area: upDown;
`;

interface TextAreaFieldFormProps {
  field: Field;
  sectionUuid: string;
  dispatch: (action: Action) => void;
}

const TextAreaFieldForm: React.FC<TextAreaFieldFormProps> = ({ sectionUuid, field, dispatch }) => {
  const handleOnChangeDefaultValue = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value: defaultValue } = e.target;
      dispatch({ type: 'set-field-default-value', sectionUuid, field, defaultValue });
    },
    [dispatch, sectionUuid, field],
  );

  const handleOnChangeLabel = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const label = e.target.value;
    dispatch({ type: 'set-field-label', sectionUuid, field, label });
  };

  const handleOnChangeOptionalMandatory = (required: boolean) => {
    dispatch({ type: 'set-field-required', sectionUuid, field, required });
  };

  const handleOnClickRemoveField = () => {
    dispatch({ type: 'remove-field', sectionUuid, field });
  };

  const handleOnMoveUp = () => {
    dispatch({ type: 'move-field-up', sectionUuid, field });
  };

  const handleOnMoveDown = () => {
    dispatch({ type: 'move-field-down', sectionUuid, field });
  };

  return (
    <StyledFieldForm>
      <StyledUpDown onUp={handleOnMoveUp} onDown={handleOnMoveDown} />
      <ControlledTextField
        label="Field Label"
        size="small"
        fullWidth
        value={field.label}
        onChange={handleOnChangeLabel}
        style={{ gridArea: 'label' }}
      />
      <OptionalMandatoryChooser
        required={field.is_required}
        onChange={handleOnChangeOptionalMandatory}
        style={{ gridArea: 'isMandatory' }}
      />
      <Chip label="Text Area" style={{ gridArea: 'fieldType' }} />
      <DeleteIconButton onClick={handleOnClickRemoveField} style={{ gridArea: 'trash' }} />
      <ControlledTextField
        label="Default value"
        size="small"
        multiline
        rows="3"
        fullWidth
        value={(field.default_value && field.default_value[0]) || ''}
        onChange={handleOnChangeDefaultValue}
        style={{ gridArea: 'defaultValue' }}
      />
    </StyledFieldForm>
  );
};

export default memo(TextAreaFieldForm);
